import React, { useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import {
  Container,
  Grid,
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import Link from 'gui/components/link'
import SectionAccordion from 'gui/components/accordion/section-accordion'
import { getLanguageChipClass } from 'services/languages'
import { getContentDetailHref } from 'services/content'
import { PublishedStatusIcon } from 'gui/components/common/PublishedStatusIcon'
import { ContentDetailLoader } from './content-detail-loader'
import { GET_SERIES_EPISODES } from './queries'

const ALL_SEASONS = 'All Seasons'

export interface EpisodeRow {
  id: string | number
  contentId?: number
  contentType: string
  title?: string
  language?: string
  published?: boolean
  seasonNumber?: number
  episodeNumber?: number
}

export interface EpisodesSectionProps {
  seriesId: string | number
  language: string
}

/**
 * Episodes section for the series detail V2 page.
 * Fetches child episodes via GraphQL and displays a season-filterable table.
 * Episodes are filtered to the series language so only one translation per episode is returned.
 */
function EpisodesSection ({ seriesId, language }: EpisodesSectionProps) {
  const [seasonFilter, setSeasonFilter] = useState<string>(ALL_SEASONS)

  const { loading, error, data } = useQuery(GET_SERIES_EPISODES, {
    variables: { id: seriesId, language },
    skip: seriesId === undefined || seriesId === null,
  })

  const rawEpisodes = useMemo(
    () => (data?.contentList?.content?.[0]?.contentEpisodes ?? []) as EpisodeRow[],
    [data],
  )

  const sortedEpisodes = useMemo(
    () =>
      [...rawEpisodes].sort((a, b) => {
        const seasonA = a.seasonNumber ?? 0
        const seasonB = b.seasonNumber ?? 0
        if (seasonA !== seasonB) return seasonA - seasonB
        const epA = a.episodeNumber ?? 0
        const epB = b.episodeNumber ?? 0
        return epA - epB
      }),
    [rawEpisodes],
  )

  const seasonOptions = useMemo(() => {
    const seasons = new Set<number>()
    rawEpisodes.forEach((ep) => {
      if (ep.seasonNumber != null) seasons.add(ep.seasonNumber)
    })
    return [ALL_SEASONS, ...Array.from(seasons).sort((a, b) => a - b)]
  }, [rawEpisodes])

  const filteredEpisodes = useMemo(() => {
    if (seasonFilter === ALL_SEASONS) return sortedEpisodes
    const seasonNum = Number(seasonFilter)
    if (Number.isNaN(seasonNum)) return sortedEpisodes
    return sortedEpisodes.filter((ep) => ep.seasonNumber === seasonNum)
  }, [sortedEpisodes, seasonFilter])

  return (
    <Container className="content-detail__section episodes-section" maxWidth={false}>
      <SectionAccordion title="Episodes" collapsed={false}>
        {loading && <ContentDetailLoader />}
        {!loading && error && <Box sx={{ py: 2, color: 'error.main' }}>{error.message}</Box>}
        {!loading && !error && (
          <>
            <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
              <Grid item xs={12} sm={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="episodes-season-label">Season</InputLabel>
                  <Select
                    labelId="episodes-season-label"
                    label="Season"
                    value={seasonFilter}
                    onChange={(e) => setSeasonFilter(e.target.value)}
                  >
                    {seasonOptions.map((season) => (
                      <MenuItem key={season} value={season}>
                        {season}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Box>
              <Grid
                container
                spacing={1}
                sx={{
                  borderBottom: '1px solid #e0e0e0',
                  pb: 1,
                  mb: 1,
                  fontWeight: 'bold',
                }}
              >
                <Grid item xs={1} sx={{ textAlign: 'right', pr: 2 }}>
                  Season
                </Grid>
                <Grid item xs={1} sx={{ textAlign: 'right', pr: 2 }}>
                  Episode
                </Grid>
                <Grid item xs={8}>
                  Title
                </Grid>
                <Grid item xs={1} sx={{ textAlign: 'center' }}>
                  Language
                </Grid>
                <Grid item xs={1} sx={{ textAlign: 'center' }}>
                  Published
                </Grid>
              </Grid>
              {filteredEpisodes.length === 0 ? (
                <Box sx={{ py: 2, color: 'text.secondary' }}>No episodes found.</Box>
              ) : (
                filteredEpisodes.map((episode, index) => (
                  <Grid
                    container
                    spacing={1}
                    key={`episode-row-${episode.id}`}
                    sx={{
                      py: 1,
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f7f7f7',
                      '&:hover': { backgroundColor: '#eef4fb' },
                    }}
                  >
                    <Grid item xs={1} sx={{ textAlign: 'right', pr: 2 }}>
                      {episode.seasonNumber ?? '—'}
                    </Grid>
                    <Grid item xs={1} sx={{ textAlign: 'right', pr: 2 }}>
                      {episode.episodeNumber ?? '—'}
                    </Grid>
                    <Grid item xs={8}>
                      <Link href={getContentDetailHref(episode.id, episode.contentType)}>
                        {episode.title?.trim() || 'Untitled'}
                      </Link>
                    </Grid>
                    <Grid
                      item
                      xs={1}
                      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    >
                      {episode.language ? (
                        <Chip
                          label={episode.language}
                          size="small"
                          variant="outlined"
                          className={`language-chip ${getLanguageChipClass(episode.language)}`}
                          sx={{ marginTop: -1.2 }}
                        />
                      ) : (
                        '—'
                      )}
                    </Grid>
                    <Grid
                      item
                      xs={1}
                      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    >
                      <PublishedStatusIcon published={episode.published ?? false} />
                    </Grid>
                  </Grid>
                ))
              )}
            </Box>
          </>
        )}
      </SectionAccordion>
    </Container>
  )
}

export default EpisodesSection
